/**
 * ShareButton - Share and embed functionality for spaces
 *
 * Provides:
 * - Copy shareable link
 * - Copy embed code
 * - Social share options
 */

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';

// compact: icon-only corner pill (live rail cards, Nina 08-06) — parent owns
// positioning; the share modal is unchanged (it portals to <body> either way).
// 🏡⋯ controlled mode (W2d, a ported room's place page): `open` + `onOpenChange`
// drive the modal from a menu item, and `hideTrigger` drops the button (the
// ⋯ menu is the door). Omit `open` and it is the old self-contained button.
const ShareButton = ({ gameId, gameName, onShare, theme, variant = 'dark', fullWidth = false, compact = false, open, onOpenChange, hideTrigger = false }) => {
  const [showModalOwn, setShowModalOwn] = useState(false);
  const controlled = open !== undefined;
  const showModal = controlled ? !!open : showModalOwn;
  const setShowModal = (v) => { if (!controlled) setShowModalOwn(v); else if (onOpenChange) onOpenChange(v); };
  const [copied, setCopied] = useState(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 600 : false,
  );
  const isLight = variant === 'light';

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Generate URLs based on current host
  const baseUrl = window.location.origin;
  const id = encodeURIComponent(gameId || gameName);
  const shareUrl = `${baseUrl}/game-step?gameId=${id}`;
  const embedUrl = `${baseUrl}/embed/${id}`;
  const embedCode = `<iframe src="${embedUrl}" width="100%" height="600" frameborder="0" allow="autoplay" style="border-radius: 12px;"></iframe>`;

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
      onShare?.(type);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const styles = {
    button: isLight ? {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px',
      padding: '8px 12px',
      width: fullWidth ? '100%' : 'auto',
      marginTop: '6px',
      background: 'transparent',
      color: '#1976d2',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '0.78rem',
      fontWeight: 500,
      transition: 'background-color 0.15s ease, color 0.15s ease',
    } : {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      padding: '6px 10px',
      background: 'rgba(255, 255, 255, 0.12)',
      color: 'rgba(255,255,255,0.85)',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '0.75rem',
      fontWeight: 600,
      transition: 'all 0.2s ease',
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 10000
    },
    modalContent: {
      backgroundColor: isLight ? '#ffffff' : '#1f2937',
      borderRadius: '16px',
      padding: isMobile ? '16px' : '24px',
      width: isMobile ? '94%' : '90%',
      maxWidth: '500px',
      maxHeight: '80vh',
      overflowY: 'auto',
      boxShadow: isLight ? '0 20px 50px rgba(0,0,0,0.18)' : 'none',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px'
    },
    title: {
      margin: 0,
      fontSize: '20px',
      fontWeight: 600,
      color: isLight ? '#111827' : '#fff'
    },
    closeButton: {
      background: 'none',
      border: 'none',
      color: isLight ? '#6b7280' : '#9ca3af',
      fontSize: '26px',
      cursor: 'pointer',
      // 40×40 tap target (was ~26×32 — Nina 07-20: X barely tappable on
      // mobile); visual glyph unchanged
      width: '40px',
      height: '40px',
      padding: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '-8px -8px 0 0',
    },
    section: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: 500,
      color: isLight ? '#4b5563' : '#9ca3af',
      marginBottom: '8px'
    },
    inputGroup: {
      display: 'flex',
      gap: '8px'
    },
    input: {
      flex: 1,
      minWidth: 0,
      padding: '10px 12px',
      backgroundColor: isLight ? '#f5f5f7' : '#374151',
      border: isLight ? '1px solid #d1d5db' : '1px solid #4b5563',
      borderRadius: '8px',
      color: isLight ? '#111827' : '#fff',
      fontSize: '14px',
      fontFamily: 'monospace',
      boxSizing: 'border-box',
    },
    copyButton: {
      padding: '10px 16px',
      backgroundColor: isLight ? '#1976d2' : '#4b5563',
      color: '#fff',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 500,
      whiteSpace: 'nowrap',
      transition: 'background-color 0.2s'
    },
    copiedButton: {
      backgroundColor: '#22c55e'
    },
    textarea: {
      width: '100%',
      padding: '10px 12px',
      backgroundColor: isLight ? '#f5f5f7' : '#374151',
      border: isLight ? '1px solid #d1d5db' : '1px solid #4b5563',
      borderRadius: '8px',
      color: isLight ? '#111827' : '#fff',
      fontSize: '12px',
      fontFamily: 'monospace',
      resize: 'vertical',
      minHeight: '80px',
      boxSizing: 'border-box',
    },
    socialButtons: {
      display: 'flex',
      gap: '10px',
      flexWrap: 'wrap'
    },
    socialButton: {
      padding: isMobile ? '8px 12px' : '10px 16px',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: 500,
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    }
  };

  return (
    <>
      {!hideTrigger && (
      <button
        aria-label="Share"
        title="Share"
        style={compact ? {
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 26,
          height: 22,
          padding: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          color: '#efeff1',
          border: 'none',
          borderRadius: '2px',
          cursor: 'pointer',
          transition: 'background-color 0.15s ease',
        } : styles.button}
        onClick={(e) => { e.stopPropagation(); setShowModal(true); }}
        onMouseOver={(e) => {
          e.currentTarget.style.background = compact ? 'rgba(0,0,0,0.9)' : (isLight ? 'rgba(25, 118, 210, 0.08)' : 'rgba(255, 255, 255, 0.2)');
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.background = compact ? 'rgba(0,0,0,0.7)' : (isLight ? 'transparent' : 'rgba(255, 255, 255, 0.12)');
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
          <polyline points="16 6 12 2 8 6" />
          <line x1="12" y1="2" x2="12" y2="15" />
        </svg>
        {!compact && 'Share'}
      </button>
      )}

      {/* Portal to <body>: the explore cards render this button inside a
          `perspective` flip wrapper, which re-anchors position:fixed to the
          CARD and scopes z-index to the card's stacking context — the modal
          only looked viewport-centered because phone cards are near
          full-width, and later siblings could shadow the ✕ (Nina 07-20:
          "the X doesn't really work on mobile"). React portals still bubble
          events through the REACT tree, so the stopPropagation guards below
          keep card flips suppressed exactly as before. */}
      {showModal && ReactDOM.createPortal(
        <div style={styles.modal} onClick={(e) => { e.stopPropagation(); setShowModal(false); }}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.header}>
              <h2 style={styles.title}>Share Space</h2>
              <button style={styles.closeButton} onClick={() => setShowModal(false)}>
                &times;
              </button>
            </div>

            <div style={styles.section}>
              <label style={styles.label}>Shareable Link</label>
              <div style={styles.inputGroup}>
                <input
                  style={styles.input}
                  value={shareUrl}
                  readOnly
                  onClick={(e) => e.target.select()}
                />
                <button
                  style={{
                    ...styles.copyButton,
                    ...(copied === 'link' ? styles.copiedButton : {})
                  }}
                  onClick={() => copyToClipboard(shareUrl, 'link')}
                >
                  {copied === 'link' ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            <div style={styles.section}>
              <label style={styles.label}>Embed Code</label>
              <textarea
                style={styles.textarea}
                value={embedCode}
                readOnly
                onClick={(e) => e.target.select()}
              />
              <button
                style={{
                  ...styles.copyButton,
                  marginTop: '8px',
                  ...(copied === 'embed' ? styles.copiedButton : {})
                }}
                onClick={() => copyToClipboard(embedCode, 'embed')}
              >
                {copied === 'embed' ? 'Copied!' : 'Copy Embed Code'}
              </button>
            </div>

            <div style={styles.section}>
              <label style={styles.label}>Share on Social</label>
              <div style={styles.socialButtons}>
                <button
                  style={{ ...styles.socialButton, backgroundColor: '#1DA1F2', color: '#fff' }}
                  onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(`Check out this space: ${gameName}`)}`, '_blank')}
                >
                  {isMobile ? 'X' : 'Twitter/X'}
                </button>
                <button
                  style={{ ...styles.socialButton, backgroundColor: '#4267B2', color: '#fff' }}
                  onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank')}
                >
                  {isMobile ? 'FB' : 'Facebook'}
                </button>
                <button
                  style={{ ...styles.socialButton, backgroundColor: '#25D366', color: '#fff' }}
                  onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Check out this space: ${shareUrl}`)}`, '_blank')}
                >
                  {isMobile ? 'WA' : 'WhatsApp'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ShareButton;
